import React, { useEffect, useMemo, useState } from "react";
import { BadgeCheck, QrCode, ScanLine } from "lucide-react";
import { toast } from "sonner";
import { useData } from "../contexts/DataContext";
import QrCodeCard from "./QrCodeCard";
import QrScannerCard from "./QrScannerCard";
import { formatDateTime } from "../lib/utils";

const flowMeta = {
  driver_presented: {
    title: "Driver trip QR",
    description: "Show this QR code to the receiving adult. It is secure, trip-linked, and expires automatically.",
  },
  recipient_presented: {
    title: "Recipient confirmation QR",
    description: "Generate your own QR code so the driver can scan and log who received the child.",
  },
};

export const HandoverPanel = ({ trip, role, onRefresh }) => {
  const { generateHandoverToken, verifyHandoverToken, loadHandoverLogs, handoverLogs } = useData();
  const [tokenState, setTokenState] = useState({ driver_presented: null, recipient_presented: null });
  const [loadingFlow, setLoadingFlow] = useState(null);
  const [verifying, setVerifying] = useState(false);

  useEffect(() => {
    if (trip?.id) {
      loadHandoverLogs(trip.id);
    }
  }, [trip?.id, loadHandoverLogs]);

  const relevantLogs = useMemo(
    () => handoverLogs.filter((entry) => entry.trip_id === trip?.id).slice(0, 5),
    [handoverLogs, trip?.id]
  );

  const generateToken = async (flow) => {
    setLoadingFlow(flow);
    const result = await generateHandoverToken(trip.id, flow);
    setLoadingFlow(null);
    if (result.success) {
      setTokenState((current) => ({ ...current, [flow]: result.data }));
      toast.success("Secure handover QR generated.");
    } else {
      toast.error(result.error);
    }
  };

  const handleVerify = async (token, method) => {
    setVerifying(true);
    const result = await verifyHandoverToken(token, method);
    setVerifying(false);
    if (result.success) {
      toast.success("Handover verification recorded.");
      await Promise.all([loadHandoverLogs(trip.id), onRefresh?.()]);
      setTokenState({ driver_presented: null, recipient_presented: null });
    } else {
      toast.error(result.error);
    }
  };

  if (!trip) {
    return null;
  }

  const showDriverQr = ["driver", "admin"].includes(role);
  const showRecipientQr = ["parent", "school", "admin"].includes(role);
  const showDriverScanner = ["parent", "school", "admin"].includes(role);
  const showRecipientScanner = ["driver", "admin"].includes(role);

  return (
    <div className="space-y-6">
      <div className="guardian-card bg-white p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h2 className="font-['Manrope'] text-xl font-semibold text-[#0F172A]">Handover verification</h2>
            <p className="mt-1 text-sm leading-6 text-[#64748B]">
              Safeguarding confirmation for {trip.child_name}. Use the default trip QR flow or switch to recipient-side confirmation when needed.
            </p>
          </div>
          {trip.handover_verified_at ? (
            <div className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm font-medium text-emerald-800">
              <BadgeCheck size={16} />
              Verified {formatDateTime(trip.handover_verified_at)}
            </div>
          ) : (
            <div className="rounded-full border border-amber-200 bg-amber-50 px-3 py-2 text-sm font-medium text-amber-800">
              Pending verification
            </div>
          )}
        </div>

        <div className="mt-6 grid gap-4 lg:grid-cols-2">
          {showDriverQr && (
            <div className="space-y-4">
              <div className="flex items-center justify-between gap-3">
                <div className="inline-flex items-center gap-2 text-sm font-semibold text-[#163D6B]"><QrCode size={16} /> Primary flow</div>
                <button
                  onClick={() => generateToken("driver_presented")}
                  className="rounded-xl bg-[#163D6B] px-4 py-2 text-sm font-semibold text-white"
                >
                  {loadingFlow === "driver_presented" ? "Generating…" : "Generate trip QR"}
                </button>
              </div>
              <QrCodeCard
                tokenData={tokenState.driver_presented}
                title={flowMeta.driver_presented.title}
                description={flowMeta.driver_presented.description}
                onRefresh={() => generateToken("driver_presented")}
                refreshing={loadingFlow === "driver_presented"}
              />
            </div>
          )}

          {showRecipientQr && (
            <div className="space-y-4">
              <div className="flex items-center justify-between gap-3">
                <div className="inline-flex items-center gap-2 text-sm font-semibold text-[#163D6B]"><QrCode size={16} /> Recipient-side option</div>
                <button
                  onClick={() => generateToken("recipient_presented")}
                  className="rounded-xl border border-[#E2E8F0] bg-white px-4 py-2 text-sm font-semibold text-[#0F172A]"
                >
                  {loadingFlow === "recipient_presented" ? "Generating…" : "Generate my QR"}
                </button>
              </div>
              <QrCodeCard
                tokenData={tokenState.recipient_presented}
                title={flowMeta.recipient_presented.title}
                description={flowMeta.recipient_presented.description}
                onRefresh={() => generateToken("recipient_presented")}
                refreshing={loadingFlow === "recipient_presented"}
              />
            </div>
          )}
        </div>
      </div>

      <div className="grid gap-6 xl:grid-cols-2">
        {showDriverScanner && (
          <QrScannerCard
            title="Scan driver trip QR"
            description="Use the camera to scan the driver’s trip QR. Manual entry is available if your device cannot access the camera."
            onVerify={handleVerify}
            verifying={verifying}
          />
        )}

        {showRecipientScanner && (
          <QrScannerCard
            title="Scan recipient QR"
            description="Scan the recipient’s secure QR to record who received the child. Manual entry can be used if scanning is unavailable."
            onVerify={handleVerify}
            verifying={verifying}
          />
        )}
      </div>

      <div className="guardian-card bg-white p-6">
        <div className="flex items-center gap-2 text-[#163D6B]">
          <ScanLine size={18} />
          <h3 className="font-['Manrope'] text-xl font-semibold text-[#0F172A]">Recent verification log</h3>
        </div>
        <div className="mt-4 space-y-3">
          {relevantLogs.length ? (
            relevantLogs.map((entry) => (
              <div key={entry.id} className="rounded-2xl border border-[#E2E8F0] bg-[#F8FAFC] p-4 text-sm">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="font-semibold text-[#0F172A]">{entry.verified_by?.name} • {entry.verified_by?.role}</p>
                    <p className="mt-1 text-[#64748B]">Method: {entry.method} • Confirmed by {entry.confirmed_by_name || "CareFleet user"}</p>
                  </div>
                  <p className="text-xs text-[#64748B]">{formatDateTime(entry.timestamp)}</p>
                </div>
              </div>
            ))
          ) : (
            <div className="rounded-2xl border border-dashed border-[#CBD5E1] p-4 text-sm text-[#64748B]">No handover confirmations recorded for this trip yet.</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HandoverPanel;
